import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ProtectedRoute from './components/admin/ProtectedRoute';
import ErrorBoundary from './components/admin/ErrorBoundary';
import { Spinner } from './components/ui';
import { AdminUiProvider } from './contexts/AdminUiContext';
import { AdminPreferencesProvider } from './contexts/AdminPreferencesContext';
import { NotificationCenterProvider } from './contexts/NotificationCenterContext';
import { AdminRbacProvider } from './contexts/AdminRbacContext';
import RequirePermission from './components/admin/RequirePermission';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { ADMIN_ROUTE_PERMISSIONS } from './rbac/routePermissions';

const Notfound = lazyWithRetry(() => import('./pages/Notfound'));
const Forbidden = lazyWithRetry(() => import('./pages/Forbidden'));
const AdminLogin = lazyWithRetry(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/AdminDashboard'));
const UserManagement = lazyWithRetry(() => import('./pages/admin/UserManagement'));
const KYCManagement = lazyWithRetry(() => import('./pages/admin/KYCManagement'));
const RoleManagement = lazyWithRetry(() => import('./pages/admin/RoleManagement'));
const ContentManagement = lazyWithRetry(() => import('./pages/admin/ContentManagement'));
const PayoutManagement = lazyWithRetry(() => import('./pages/admin/PayoutManagement'));
const SubscriptionManagement = lazyWithRetry(() => import('./pages/admin/SubscriptionManagement'));
const LogsManagement = lazyWithRetry(() => import('./pages/admin/LogsManagement'));
const CJProductsManagement = lazyWithRetry(() => import('./pages/admin/CJProductsManagement'));
const OrdersManagement = lazyWithRetry(() => import('./pages/admin/OrdersManagement'));
const AdsManagement = lazyWithRetry(() => import('./pages/admin/AdsManagement'));
const WebhookBillingManagement = lazyWithRetry(() => import('./pages/admin/WebhookBillingManagement'));
const PaymentActivityManagement = lazyWithRetry(() => import('./pages/admin/PaymentActivityManagement'));
const FinanceDashboardManagement = lazyWithRetry(() => import('./pages/admin/FinanceDashboardManagement'));
const FinanceLedgerManagement = lazyWithRetry(() => import('./pages/admin/FinanceLedgerManagement'));
const FinanceReconciliationManagement = lazyWithRetry(
  () => import('./pages/admin/FinanceReconciliationManagement')
);
const FinanceChargesManagement = lazyWithRetry(() => import('./pages/admin/FinanceChargesManagement'));
const FinanceBookingPayoutsManagement = lazyWithRetry(
  () => import('./pages/admin/FinanceBookingPayoutsManagement')
);
const AiInfrastructureDashboard = lazyWithRetry(() => import('./pages/admin/AiInfrastructureDashboard'));
const PlansPricingManagement = lazyWithRetry(() => import('./pages/admin/PlansPricingManagement'));
const BusinessAnalyticsManagement = lazyWithRetry(() => import('./pages/admin/BusinessAnalyticsManagement'));
const FeatureFlagsManagement = lazyWithRetry(() => import('./pages/admin/FeatureFlagsManagement'));
const ModerationManagement = lazyWithRetry(() => import('./pages/admin/ModerationManagement'));
const VerificationQueueManagement = lazyWithRetry(
  () => import('./pages/admin/VerificationQueueManagement')
);
const DisputesManagement = lazyWithRetry(() => import('./pages/admin/DisputesManagement'));
const RentalAgreementsManagement = lazyWithRetry(
  () => import('./pages/admin/RentalAgreementsManagement')
);
const CmsPagesManagement = lazyWithRetry(() => import('./pages/admin/CmsPagesManagement'));
const CategoriesManagement = lazyWithRetry(() => import('./pages/admin/CategoriesManagement'));
const ApplicationSettingsManagement = lazyWithRetry(
  () => import('./pages/admin/ApplicationSettingsManagement')
);
const RewardsManagement = lazyWithRetry(() => import('./pages/admin/RewardsManagement'));
const ReferralsManagement = lazyWithRetry(() => import('./pages/admin/ReferralsManagement'));
const CreatorRewardsManagement = lazyWithRetry(
  () => import('./pages/admin/CreatorRewardsManagement')
);
const LiveCenterManagement = lazyWithRetry(() => import('./pages/admin/LiveCenterManagement'));
const CalendarAdminHub = lazyWithRetry(() => import('./pages/admin/calendar/CalendarAdminHub'));
const BusinessesHub = lazyWithRetry(() => import('./pages/admin/BusinessesHub'));
const MarketplaceHub = lazyWithRetry(() => import('./pages/admin/MarketplaceHub'));

function PageFallback() {
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
      <Spinner />
    </div>
  );
}

function Guarded({ path, children }: { path: string; children: React.ReactNode }) {
  const permission = ADMIN_ROUTE_PERMISSIONS[path];
  if (!permission) return <>{children}</>;
  return <RequirePermission permission={permission}>{children}</RequirePermission>;
}

/** Persistent providers for all protected admin pages (no remount on navigation). */
function ProtectedAdminLayout() {
  return (
    <ProtectedRoute>
      <AdminRbacProvider>
        <AdminUiProvider>
          <AdminPreferencesProvider>
            <NotificationCenterProvider>
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </NotificationCenterProvider>
          </AdminPreferencesProvider>
        </AdminUiProvider>
      </AdminRbacProvider>
    </ProtectedRoute>
  );
}

const App = () => {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<ProtectedAdminLayout />}>
          <Route path="/admin/forbidden" element={<Forbidden />} />
          <Route path="/admin" element={<Guarded path="/admin"><AdminDashboard /></Guarded>} />
          <Route path="/admin/users" element={<Guarded path="/admin/users"><UserManagement /></Guarded>} />
          <Route path="/admin/kyc" element={<Guarded path="/admin/kyc"><KYCManagement /></Guarded>} />
          <Route path="/admin/roles" element={<Guarded path="/admin/roles"><RoleManagement /></Guarded>} />
          <Route path="/admin/content" element={<Guarded path="/admin/content"><ContentManagement /></Guarded>} />
          <Route path="/admin/businesses" element={<Guarded path="/admin/businesses"><BusinessesHub /></Guarded>} />
          <Route path="/admin/marketplace" element={<Guarded path="/admin/marketplace"><MarketplaceHub /></Guarded>} />
          <Route path="/admin/payouts" element={<Guarded path="/admin/payouts"><PayoutManagement /></Guarded>} />
          <Route path="/admin/payment-activity" element={<Guarded path="/admin/payment-activity"><PaymentActivityManagement /></Guarded>} />
          <Route path="/admin/finance-dashboard" element={<Guarded path="/admin/finance-dashboard"><FinanceDashboardManagement /></Guarded>} />
          <Route path="/admin/finance-ledger" element={<Guarded path="/admin/finance-ledger"><FinanceLedgerManagement /></Guarded>} />
          <Route path="/admin/finance-reconciliation" element={<Guarded path="/admin/finance-reconciliation"><FinanceReconciliationManagement /></Guarded>} />
          <Route path="/admin/finance-booking-payouts" element={<Guarded path="/admin/finance-booking-payouts"><FinanceBookingPayoutsManagement /></Guarded>} />
          <Route path="/admin/finance-charges" element={<Guarded path="/admin/finance-charges"><FinanceChargesManagement /></Guarded>} />
          <Route path="/admin/cj-products" element={<Guarded path="/admin/cj-products"><CJProductsManagement /></Guarded>} />
          <Route path="/admin/orders" element={<Guarded path="/admin/orders"><OrdersManagement /></Guarded>} />
          <Route path="/admin/subscriptions" element={<Guarded path="/admin/subscriptions"><SubscriptionManagement /></Guarded>} />
          <Route path="/admin/logs" element={<Guarded path="/admin/logs"><LogsManagement /></Guarded>} />
          <Route path="/admin/ads" element={<Guarded path="/admin/ads"><AdsManagement /></Guarded>} />
          <Route path="/admin/rewards" element={<Guarded path="/admin/rewards"><RewardsManagement /></Guarded>} />
          <Route path="/admin/referrals" element={<Guarded path="/admin/referrals"><ReferralsManagement /></Guarded>} />
          <Route path="/admin/creator-rewards" element={<Guarded path="/admin/creator-rewards"><CreatorRewardsManagement /></Guarded>} />
          <Route path="/admin/live-center" element={<Guarded path="/admin/live-center"><LiveCenterManagement /></Guarded>} />
          <Route path="/admin/calendar" element={<Guarded path="/admin/calendar"><CalendarAdminHub /></Guarded>} />
          <Route path="/admin/billing-webhooks" element={<Guarded path="/admin/billing-webhooks"><WebhookBillingManagement /></Guarded>} />
          <Route path="/admin/ai-infrastructure" element={<Guarded path="/admin/ai-infrastructure"><AiInfrastructureDashboard /></Guarded>} />
          <Route path="/admin/plans-pricing" element={<Guarded path="/admin/plans-pricing"><PlansPricingManagement /></Guarded>} />
          <Route path="/admin/analytics" element={<Guarded path="/admin/analytics"><BusinessAnalyticsManagement /></Guarded>} />
          <Route path="/admin/feature-flags" element={<Guarded path="/admin/feature-flags"><FeatureFlagsManagement /></Guarded>} />
          <Route path="/admin/moderation" element={<Guarded path="/admin/moderation"><ModerationManagement /></Guarded>} />
          <Route path="/admin/verification-queue" element={<Guarded path="/admin/verification-queue"><VerificationQueueManagement /></Guarded>} />
          <Route path="/admin/disputes" element={<Guarded path="/admin/disputes"><DisputesManagement /></Guarded>} />
          <Route path="/admin/rental-agreements" element={<Guarded path="/admin/rental-agreements"><RentalAgreementsManagement /></Guarded>} />
          <Route path="/admin/cms" element={<Guarded path="/admin/cms"><CmsPagesManagement /></Guarded>} />
          <Route path="/admin/categories" element={<Guarded path="/admin/categories"><CategoriesManagement /></Guarded>} />
          <Route path="/admin/app-settings" element={<Guarded path="/admin/app-settings"><ApplicationSettingsManagement /></Guarded>} />
        </Route>
        <Route path="/admin/finance-commission-settings" element={<Navigate to="/admin/finance-charges" replace />} />
        <Route path="*" element={<Notfound />} />
      </Routes>
    </Suspense>
  );
};

export default App;
